import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Typography,
  Box,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { statsService } from "../../utils/services/stats-service";
import { buildChartData } from "../../utils/utility";
import { getISOWeek } from "date-fns";
import { CalendarIcon } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { isMobile } from "../../globalStyles/mobile";
import { useDarkMode } from "../context/darkModeContext";

const COMPARE_TYPES = [
  { value: "DOD", label: "Day-over-Day(DoD)" },
  { value: "WOW", label: "Week-over-Week(WoW)" },
  { value: "MOM", label: "Month-over-Month(MoM)" },
  { value: "YOY", label: "Year-over-Year(YoY)" },
  { value: "WNY", label: "YoY Weekly" },
  { value: "MNY", label: "YoY Monthly" },
];
const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Helper function to get dynamic labels based on compare mode
const getLabelsByCompareMode = (
  compareMode,
  selectedMonth,
  selectedYear,
  selectedWeekNo,
) => {
  const today = new Date();
  switch (compareMode) {
    case "DOD":
      return {
        current: "Today",
        last: "Yesterday",
      };
    case "WOW":
      return {
        current: "This Week",
        last: "Last Week",
      };
    case "MOM":
      const currentMonthName = monthNames[selectedMonth - 1];
      const previousMonthIndex =
        selectedMonth - 2 >= 0 ? selectedMonth - 2 : 11;
      const previousMonthName = monthNames[previousMonthIndex];
      const previousMonthYear =
        selectedMonth === 1 ? selectedYear - 1 : selectedYear;

      return {
        current: `${currentMonthName} ${selectedYear}`,
        last: `${previousMonthName} ${previousMonthYear}`,
      };
    case "YOY":
      return {
        current: `${selectedYear}`,
        last: `${selectedYear - 1}`,
      };
    case "WNY":
      return {
        current: `Week #${selectedWeekNo} ${selectedYear}`,
        last: `Week #${selectedWeekNo} ${selectedYear - 1}`,
      };
    case "MNY":
      const monthName = monthNames[selectedMonth - 1];
      return {
        current: `${monthName} ${selectedYear}`,
        last: `${monthName} ${selectedYear - 1}`,
      };
    default:
      return {
        current: "This Period",
        last: "Last Period",
      };
  }
};

const LAST_PERIOD_COLOR = "#2196F3"; // Blue
const THIS_PERIOD_COLOR = "#1A7F78"; // Teal

export default function OrderFillRate({
  client_id,
  selectedDate: propSelectedDate,
}) {
  const { colors } = useDarkMode();

  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedWeekNo, setSelectedWeekNo] = useState(getISOWeek(today));
  const [compareMode, setCompareMode] = useState("WOW");
  const [chartData, setChartData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(today.setMonth(today.getMonth() - 2)),
    endDate: new Date(new Date().setDate(new Date().getDate() + 2)),
    key: "selection",
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);

  // State for dynamic chart and legend labels
  const [periodLabels, setPeriodLabels] = useState({
    current: "This Period",
    last: "Last Period",
  });

  // Fetch chart data from API
  // Update period labels whenever compare mode or related parameters change
  useEffect(() => {
    const dynamicLabels = getLabelsByCompareMode(
      compareMode,
      selectedMonth,
      selectedYear,
      selectedWeekNo,
    );
    setPeriodLabels(dynamicLabels);
  }, [compareMode, selectedMonth, selectedYear, selectedWeekNo]);

  useEffect(() => {
    async function fetchChartData() {
      try {
        const params = {
          mode: compareMode,
          client_id: client_id,
          month: selectedMonth,
          year: selectedYear,
          weekNo: selectedWeekNo,
          selectedDate: compareMode === "YOY" ? propSelectedDate : selectedDate,
        };
        // if (compareMode === "WOW") {
        //   params.fromISO = selectionRange.startDate;
        //   params.toISO = selectionRange.endDate;
        // }

        const response = await statsService.order_fill_rate(params);
        // buildChartData returns { labels, datasets }, convert to recharts format
        const built = buildChartData(response, {
          currentLabel: periodLabels.current,
          compareLabel: periodLabels.last,
          compareMode: compareMode,
        });
        // Convert to recharts format: [{ day, last, current }]
        const rechartsData = built.labels.map((label, idx) => ({
          day: label,
          last: built.datasets[0]?.data[idx] ?? 0,
          current: built.datasets[1]?.data[idx] ?? 0,
        }));
        setChartData(rechartsData);
      } catch (err) {
        console.error("orders_fill_rate", err);
      }
    }
    fetchChartData();
  }, [
    compareMode,
    selectionRange,
    selectedMonth,
    selectedYear,
    selectedWeekNo,
    selectedDate,
    periodLabels,
    client_id,
    propSelectedDate,
  ]);

  // Close calendar on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarContainerRef.current &&
        !calendarContainerRef.current.contains(event.target) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    }
    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  // Custom dot for both lines
  const renderDot = (color) => (props) => {
    const { cx, cy } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        stroke={color}
        strokeWidth={2}
        fill="#fff"
      />
    );
  };

  // Get dynamic labels based on compare mode
  const { current: currentLabel, last: lastLabel } = getLabelsByCompareMode(
    compareMode,
    selectedMonth,
    selectedYear,
    selectedWeekNo,
  );

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: 0,
        width: "100%",
        minHeight: 350,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.bgPaper,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ fontSize: isMobile() ? 12 : 14 }}
        >
          {`ORDERS FILL RATE – TREND CHART`}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Compare</InputLabel>
            <Select
              label="Compare"
              value={compareMode}
              onChange={(e) => setCompareMode(e.target.value)}
            >
              {COMPARE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {compareMode === "DOD" && (
            <Box sx={{ minWidth: 120 }}>
              <TextField
                autoComplete="off"
                name="date-field"
                margin="dense"
                label="Select date"
                size="small"
                value={
                  selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : ""
                }
                onFocus={() => setShowCalendar(!showCalendar)}
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        onClick={() => setShowCalendar(!showCalendar)}
                      >
                        <CalendarIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: colors.bgPaper }}
              />
            </Box>
          )}
          {showCalendar && (
            <Box
              ref={calendarContainerRef}
              sx={{
                position: "absolute",
                top: 36,
                right: 0,
                zIndex: 99999,
                bgcolor: colors.bgPaper,
                boxShadow: 3,
                borderRadius: 1,
                p: 1,
                width: 300,
                "& .react-calendar": {
                  width: "100%",
                },
              }}
            >
              <Calendar
                onChange={(date) => {
                  setSelectedDate(date);
                  setShowCalendar(false);
                }}
                value={selectedDate}
                maxDate={new Date()}
                locale="en-US"
                className="custom-calendar"
                defaultActiveStartDate={selectedDate ?? new Date()}
              />
            </Box>
          )}
          {compareMode === "MNY" && (
            <>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  label="Month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {monthNames.map((m, idx) => (
                    <MenuItem key={idx + 1} value={idx + 1}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {[today.getFullYear() - 1, today.getFullYear()].map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {compareMode === "MOM" && (
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Month</InputLabel>
              <Select
                label="Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {monthNames.map((m, idx) => (
                  <MenuItem key={idx + 1} value={idx + 1}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {compareMode === "WNY" && (
            <>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Week #</InputLabel>
                <Select
                  label="Week #"
                  value={selectedWeekNo}
                  onChange={(e) => setSelectedWeekNo(e.target.value)}
                >
                  {Array.from({ length: 52 }, (_, i) => i + 1).map((w) => (
                    <MenuItem key={w} value={w}>
                      {w}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {[today.getFullYear() - 1, today.getFullYear()].map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Stack>
      </Stack>
      <Box sx={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              stroke={colors.gridLine}
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: colors.textSecondary }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: colors.textTertiary }}
              domain={[0, 100]}
              ticks={[0, 20, 40, 60, 80, 100]}
            />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="last"
              name={periodLabels.last}
              stroke={LAST_PERIOD_COLOR}
              fill="url(#colorLast)"
              fillOpacity={0.18}
              activeDot={renderDot(LAST_PERIOD_COLOR)}
              dot={renderDot(LAST_PERIOD_COLOR)}
            />
            <Area
              type="monotone"
              dataKey="current"
              name={periodLabels.current}
              stroke={THIS_PERIOD_COLOR}
              fill="url(#colorCurrent)"
              fillOpacity={0.18}
              activeDot={renderDot(THIS_PERIOD_COLOR)}
              dot={renderDot(THIS_PERIOD_COLOR)}
            />
            <defs>
              <linearGradient id="colorLast" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={LAST_PERIOD_COLOR}
                  stopOpacity={0.18}
                />
                <stop
                  offset="100%"
                  stopColor={LAST_PERIOD_COLOR}
                  stopOpacity={0.03}
                />
              </linearGradient>
              <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={THIS_PERIOD_COLOR}
                  stopOpacity={0.18}
                />
                <stop
                  offset="100%"
                  stopColor={THIS_PERIOD_COLOR}
                  stopOpacity={0.03}
                />
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      {/* Custom legend below the chart */}
      <Stack
        direction="row"
        spacing={3}
        justifyContent="center"
        alignItems="center"
        mt={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#2196F3",
              border: "2px solid #2196F3",
            }}
          />
          <Typography variant="body2" color="#2196F3" fontSize={13}>
            {periodLabels.last}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#1A7F78",
              border: "2px solid #1A7F78",
            }}
          />
          <Typography variant="body2" color="#1A7F78" fontSize={13}>
            {periodLabels.current}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}
